import cx from 'classnames';
import omit from 'lodash/omit';
import { Children, createElement, PureComponent, type ReactNode } from 'react';

import { type SIZES } from '../../constants';
import Box, { omitBoxProps, pickBoxProps } from '../box';
import { type BoxProps } from '../box/Box';
import Button from '../button';
import { type ButtonLevel } from '../button/Button';
import isComponentOfType from '../utils/is-component-of-type';
import isReactElement from '../utils/is-react-element';

import * as theme from './theme.css';

export interface ButtonGroupProps extends Omit<BoxProps, 'size' | 'onChange' | 'ref'> {
  /** The content to display inside the button group. */
  children?: ReactNode;
  /** A class name for the wrapper to give custom styles. */
  className?: string;
  /** If true, child components will be displayed segmented. */
  segmented?: boolean;
  /** The level of the buttons */
  size?: Exclude<(typeof SIZES)[number], 'fullscreen' | 'smallest' | 'hero'>;
  /** The level of the buttons */
  level?: `${ButtonLevel}`;
  /** The value of the currently active button. */
  value?: string;
  /** Callback function that is fired when the active button changes. */
  onChange?: (value: string, event: Event) => void;
}

class ButtonGroup extends PureComponent<ButtonGroupProps> {
  handleChange = (value: string, event: Event) => {
    if (this.props.onChange) {
      this.props.onChange(value, event);
    }
  };

  render() {
    const { children, className, segmented = false, value, ...others } = this.props;

    const classNames = cx(
      theme['group'],
      {
        [theme['segmented']]: segmented,
      },
      className,
    );

    return (
      <Box data-teamleader-ui="button-group" className={classNames} {...pickBoxProps(others)}>
        {Children.map(children, (child) => {
          if (!isReactElement(child) || (isReactElement(child) && !isComponentOfType(Button, child))) {
            return child;
          }

          let optionalChildProps = {};
          if (value) {
            optionalChildProps = {
              active: child.props.value === value,
              onClick: (event: Event) => this.handleChange(child.props.value || '', event),
            };
          }

          const childProps = omit(child.props, ['value']);
          const groupProps = omit(others, ['onChange']);

          return createElement(child.type, {
            ...childProps,
            ...optionalChildProps,
            ...omitBoxProps(groupProps),
          });
        })}
      </Box>
    );
  }
}

export default ButtonGroup;
